import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { FormModal } from "../form";
import { PrimaryButton } from "../Buttons";

/**
 * Multi-select of property configurations to assign to a building. Parent
 * supplies the available `configurations` and the `building` whose existing
 * assignments seed initial selection. Not a Form-driven screen — the only
 * state is a Set of selected ids — so we use FormModal as the shell and
 * keep the checkbox list inline.
 */
const AssignConfigToBuildingModal = ({
    isOpen,
    onClose,
    building,
    configurations,
    onConfigurationAssigned,
}) => {
    const [selectedConfigs, setSelectedConfigs] = useState([]);
    const [isAssigning, setIsAssigning] = useState(false);

    // Same paginated-response defensiveness as ConfigAssignmentModal: parents
    // may hand us either an array or a {items, total} wrapper.
    const configList = Array.isArray(configurations)
        ? configurations
        : Array.isArray(configurations?.items)
            ? configurations.items
            : [];

    useEffect(() => {
        if (!isOpen) return;
        const initial = (building?.assignedConfigs || []).map(
            (c) => c.propertyConfig.propertyConfigId,
        );
        setSelectedConfigs(initial);
    }, [isOpen, building]);

    const toggle = (configId) =>
        setSelectedConfigs((prev) =>
            prev.includes(configId) ? prev.filter((id) => id !== configId) : [...prev, configId],
        );

    const handleAssignConfigs = async () => {
        if (!building) {
            toast.error("No building selected.");
            return;
        }
        if (selectedConfigs.length === 0) {
            toast.warn("No configurations selected to assign.");
            return;
        }
        setIsAssigning(true);
        try {
            const requestBody = selectedConfigs.map((configId) => ({
                buildingId: building.buildingId,
                propertyConfigId: configId,
            }));
            await axiosInstance.post(`/building/property/config`, requestBody);
            toast.success("Configurations assigned successfully!");
            onConfigurationAssigned?.();
            onClose();
        } catch (error) {
            console.error("Error assigning configurations:", error);
            toast.error(error?.detail || error?.response?.data?.detail || "Error assigning configurations.");
        } finally {
            setIsAssigning(false);
        }
    };

    return (
        <FormModal
            open={isOpen}
            onClose={onClose}
            title={`Assign Configurations to ${building?.buildingName || ""}`}
            size="sm"
            footer={
                <div className="flex justify-end">
                    <PrimaryButton
                        onClick={handleAssignConfigs}
                        disabled={configList.length === 0 || isAssigning}
                    >
                        {isAssigning ? "Assigning..." : "Assign"}
                    </PrimaryButton>
                </div>
            }
        >
            {configList.length > 0 ? (
                <ul className="space-y-2">
                    {configList.map((config) => (
                        <li key={config.propertyConfigId}>
                            <label className="flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer">
                                <input
                                    type="checkbox"
                                    value={config.propertyConfigId}
                                    checked={selectedConfigs.includes(config.propertyConfigId)}
                                    onChange={() => toggle(config.propertyConfigId)}
                                    className="rounded border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
                                />
                                {config.configName}
                            </label>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="text-center text-gray-600 p-4 bg-yellow-50 rounded-md">
                    <p className="mb-1 font-semibold">No Configurations Available</p>
                    <p className="text-sm">
                        Please create configurations first before assigning to a building.
                    </p>
                </div>
            )}
        </FormModal>
    );
};

export default AssignConfigToBuildingModal;
